/**
 * RightPanel — Adaptive right panel that switches between review and batch modes.
 *
 * When in selection mode → RightPanelBatch (even with 0 selected — shows empty state)
 * Otherwise → RightPanelReview
 */
import type { Ref } from "react";
import type { CustomFieldDefinitionDto, DocumentDetailDto, UpdateCustomFieldsRequestDto } from "@/types/api/documents";
import type { GlCodeDto } from "@/types/api/gl-codes";
import type { OrganizationListItemDto } from "@/types/api/organizations";
import type { RoutingCodeDto } from "@/types/api/routing-codes";
import type { HomeDocument } from "@/types/home";
import { RightPanelBatch } from "./right-panel-batch";
import { RightPanelReview, type RightPanelReviewHandle } from "./right-panel-review";

interface RightPanelProps {
    /** Whether batch-selection mode is active */
    isSelectionMode: boolean;
    /** Currently active document for review */
    activeDocument: HomeDocument | null;
    /** Full document detail (custom fields, pages, etc.) */
    documentDetail: DocumentDetailDto | null;
    /** Whether document detail is loading */
    isDetailLoading: boolean;
    /** Custom field definitions for the active document type */
    customFieldDefinitions: CustomFieldDefinitionDto[];
    /** LineItem-scoped field definitions for the active document type's line item types */
    lineItemFieldDefinitions: CustomFieldDefinitionDto[];
    /** Selected documents for batch operations */
    selectedDocs: HomeDocument[];
    /** Current export format */
    exportFormat: string;
    /** Whether the client has a bridge configured */
    hasBridge: boolean;
    /** Whether Direct Sync export is offered (bridge exists AND export mode is DirectSync) */
    canDirectSync: boolean;
    /** Bridge name (null if no bridge) */
    bridgeName: string | null;
    /** Routing codes for dropdown (when entitlement enabled) */
    routingCodes?: RoutingCodeDto[];
    /** GL chart of accounts for the per-line gl_code dropdown (when entitlement enabled) */
    glCodes?: GlCodeDto[];
    /** Whether GL code suggestions are enabled for this client (FEATURE_GL_SUGGEST) */
    glSuggestionsEnabled?: boolean;
    /** Organizations scoped to the current user (used for org assignment dropdown) */
    orgs?: OrganizationListItemDto[];
    /** Whether the current session is an admin previewing a client */
    isAdminPreview?: boolean;
    /** Callbacks */
    onApprove: (docId: number) => void;
    onDelete: (docId: number) => void;
    onUpdateFields: (docId: number, dto: UpdateCustomFieldsRequestDto) => Promise<void>;
    onUpdateOrganization?: (docId: number, organizationId: number) => Promise<void>;
    onCsvExport: (doc: DocumentDetailDto) => Promise<void>;
    onExternalExport: (docId: number) => Promise<void>;
    /** Retry a failed external export (latest job is a failed ExternalExport) */
    onRetryExport: (docId: number) => Promise<void>;
    /** Re-trigger vendor lookup for a document whose lookup failed (BizVendorNotFound) */
    onRelookup: (docId: number) => Promise<void>;
    /** Field code of the vendor-name field; drives the failure-mode single-field view */
    vendorNameFieldCode?: string | null;
    onBatchExport: () => void;
    onBatchDelete: () => void;
    onExportFormatChange: (format: string) => void;
    /** Notify parent when field changes exist */
    onHasChangesChange?: (hasChanges: boolean) => void;
    /** Ref to imperatively trigger save on the review panel */
    reviewRef?: Ref<RightPanelReviewHandle>;
    /** Called when user clicks the duplicate file link */
    onSelectDuplicateFile?: (fileStorageId: number) => void;
    /** Re-fetch the document detail (used by the concurrency-conflict "Reload" action) */
    onReloadDetail?: (docId: number) => void;
}

export function RightPanel({
    isSelectionMode,
    activeDocument,
    documentDetail,
    isDetailLoading,
    customFieldDefinitions,
    lineItemFieldDefinitions,
    selectedDocs,
    exportFormat,
    hasBridge,
    canDirectSync,
    bridgeName,
    routingCodes,
    glCodes,
    glSuggestionsEnabled,
    orgs,
    isAdminPreview,
    onApprove,
    onDelete,
    onUpdateFields,
    onUpdateOrganization,
    onCsvExport,
    onExternalExport,
    onRetryExport,
    onRelookup,
    vendorNameFieldCode,
    onBatchExport,
    onBatchDelete,
    onExportFormatChange,
    onHasChangesChange,
    reviewRef,
    onSelectDuplicateFile,
    onReloadDetail,
}: RightPanelProps) {
    return (
        <div
            className="z-30 flex h-full flex-col border-l border-secondary bg-primary shadow-lg"
            style={{ width: "340px", minWidth: "340px", maxWidth: "340px" }}
        >
            {isSelectionMode ? (
                <RightPanelBatch
                    selectedDocs={selectedDocs}
                    selectedCount={selectedDocs.length}
                    exportFormat={exportFormat}
                    hasBridge={hasBridge}
                    canDirectSync={canDirectSync}
                    bridgeName={bridgeName}
                    onExportFormatChange={onExportFormatChange}
                    onExport={onBatchExport}
                    onDelete={onBatchDelete}
                />
            ) : (
                <RightPanelReview
                    ref={reviewRef}
                    key={activeDocument?.id}
                    document={activeDocument}
                    documentDetail={documentDetail}
                    isDetailLoading={isDetailLoading}
                    customFieldDefinitions={customFieldDefinitions}
                    lineItemFieldDefinitions={lineItemFieldDefinitions}
                    routingCodes={routingCodes}
                    glCodes={glCodes}
                    glSuggestionsEnabled={glSuggestionsEnabled}
                    orgs={orgs}
                    isAdminPreview={isAdminPreview}
                    onApprove={onApprove}
                    onDelete={onDelete}
                    onUpdateFields={onUpdateFields}
                    onUpdateOrganization={onUpdateOrganization}
                    onCsvExport={onCsvExport}
                    onExternalExport={onExternalExport}
                    onRetryExport={onRetryExport}
                    onRelookup={onRelookup}
                    vendorNameFieldCode={vendorNameFieldCode}
                    canDirectSync={canDirectSync}
                    onHasChangesChange={onHasChangesChange}
                    onSelectDuplicateFile={onSelectDuplicateFile}
                    onReloadDetail={onReloadDetail}
                />
            )}
        </div>
    );
}
